import React, { useMemo, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  Input,
  DatePicker,
  Select,
  Button as AntButton,
  Modal,
} from 'antd'
import { useQuery } from '@tanstack/react-query'
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons'
import { approvalsApi } from '@/api/services'
import type { Approval } from '@/types'
import dayjs from 'dayjs'
import { leapTablePagination } from '@/components/table/defaultPagination'
import { buildViewActionColumn, translateApprovalColumns } from '@/components/table/approvalTableColumns'
import { LeapDataTable } from '@/components/table/LeapDataTable'
import { isTableChromeTarget } from '@/components/table/tableChrome'
import { toast } from 'sonner'
import { DocumentPreviewSummary } from '@/components/approvals/DocumentPreviewSummary'
import { useAuthStore } from '@/stores/authStore'
import { resolveDocumentJourneyPath } from '@/utils/transporterNav'
import { PageShell, PageHero, DashboardPanel, FilterPanel } from '@/components/dashboard'
import { Truck } from 'lucide-react'
import type { TFunction } from 'i18next'

const englishT = ((_: string, fallback?: string) => fallback ?? '') as TFunction

const { RangePicker } = DatePicker
const { Option } = Select

/** CSV export column order — mirrors persisted approval fields */
const EXPORT_COLUMNS: { key: keyof Approval; label: string }[] = [
  { key: 'id', label: 'ID' },
  { key: 'userId', label: 'User ID' },
  { key: 'approverId', label: 'Approver ID' },
  { key: 'documentNo', label: 'Document No' },
  { key: 'materialCodeNo', label: 'Material code' },
  { key: 'year', label: 'Year' },
  { key: 'postingDate', label: 'Posting date' },
  { key: 'customerCode', label: 'Customer code' },
  { key: 'customerName', label: 'Customer name' },
  { key: 'fromLocation', label: 'From location' },
  { key: 'toLocation', label: 'To location' },
  { key: 'url', label: 'URL' },
  { key: 'tempUrl', label: 'Temp URL' },
  { key: 'newUrl', label: 'New URL' },
  { key: 'status', label: 'Status' },
  { key: 'type', label: 'Type' },
  { key: 'approval_remark', label: 'Approval remark' },
  { key: 'rejection_remark', label: 'Rejection remark' },
  { key: 'actual_qty', label: 'Actual qty' },
  { key: 'excess_qty', label: 'Excess qty' },
  { key: 'short_qty', label: 'Short qty' },
  { key: 'quantity', label: 'Quantity' },
  { key: 'vehicleNo', label: 'Vehicle no' },
  { key: 'lrNo', label: 'LR no' },
  { key: 'app', label: 'App' },
  { key: 'transporterCode', label: 'Transporter code' },
  { key: 'transporterName', label: 'Transporter name' },
  { key: 'sapResponseJson', label: 'SAP response JSON' },
  { key: 'userName', label: 'User name' },
  { key: 'userUserId', label: 'User user id' },
  { key: 'approverName', label: 'Approver name' },
  { key: 'approverUserId', label: 'Approver user id' },
  { key: 'createdAt', label: 'Created at' },
  { key: 'updatedAt', label: 'Updated at' },
]

function csvEscapeCell(raw: unknown): string {
  if (raw === null || raw === undefined) return '""'
  const s = String(raw).replace(/\r\n/g, '\n').replace(/"/g, '""')

  return `"${s}"`
}

const PendingPODReportsPage: React.FC<{
  initialStatus?: string
  fixedTransporterCode?: boolean
  pageTitle?: string
  pageDescription?: string
}> = ({ initialStatus, fixedTransporterCode, pageTitle, pageDescription }) => {
  const navigate = useNavigate()
  const { user } = useAuthStore()
  const fixedCode = fixedTransporterCode ? String(user?.transporterCode || '') : ''
  const lockedStatus = fixedTransporterCode && initialStatus ? initialStatus : null
  const [filters, setFilters] = useState({
    transporterCode: fixedCode,
    dateRange: null as [dayjs.Dayjs, dayjs.Dayjs] | null,
    status: (initialStatus || 'Pending') as string,
  })

  const resolvedTitle =
    pageTitle ||
    (lockedStatus === 'Pending'
      ? 'Pending approval'
      : lockedStatus === 'Approved'
        ? 'Approved POD'
        : 'Transporter Reports')
  const resolvedDescription =
    pageDescription ||
    (lockedStatus === 'Pending'
      ? 'POD you submitted that are awaiting approval from POD Admin'
      : lockedStatus === 'Approved'
        ? 'POD approved for your transporter code'
        : 'View and export transporter-specific POD documents')

  // If the user loads later (validateSession), ensure we backfill transporter code.
  React.useEffect(() => {
    if (!fixedTransporterCode) return
    const next = String(user?.transporterCode || '')
    if (!next) return
    setFilters((prev) => (prev.transporterCode ? prev : { ...prev, transporterCode: next }))
  }, [fixedTransporterCode, user?.transporterCode])

  // Sync and reset status filter whenever initialStatus changes (e.g. route transitions)
  React.useEffect(() => {
    setFilters((prev) => ({
      ...prev,
      status: initialStatus || 'Pending',
    }))
  }, [initialStatus])

  const [viewModalOpen, setViewModalOpen] = useState(false)
  const [viewUrl, setViewUrl] = useState<string | null>(null)
  const [viewRecord, setViewRecord] = useState<Approval | null>(null)

  const { data: approvals, isLoading, refetch } = useQuery({
    queryKey: ['transporter-approvals', filters],
    queryFn: () =>
      approvalsApi.getApprovals({
        transporterCode: filters.transporterCode || undefined,
        dateFrom: filters.dateRange?.[0]?.format('YYYY-MM-DD'),
        dateTo: filters.dateRange?.[1]?.format('YYYY-MM-DD'),
        status: filters.status || undefined,
        limit: 500,
      }),
    select: (response: any) => response.data?.data,
    enabled: fixedTransporterCode ? !!user?.transporterCode : !!filters.transporterCode,
  })

  const resolveAttachmentUrl = (record: Approval): string | null =>
    record.tempUrl || record.newUrl || record.url || null

  const handleViewDocument = (record: Approval) => {
    const url = resolveAttachmentUrl(record)
    if (!url) {
      toast.error('No attachment available')
      return
    }
    setViewRecord(record)
    setViewUrl(url)
    setViewModalOpen(true)
  }

  const goToDocument = (record: Approval) => {
    navigate(resolveDocumentJourneyPath(user, record.documentNo))
  }

  const handleSearch = () => {
    if (filters.transporterCode) {
      refetch()
    }
  }

  const fetchExportPayload = async () => {
    const axiosRes = (await approvalsApi.getApprovals({
      transporterCode: filters.transporterCode || undefined,
      dateFrom: filters.dateRange?.[0]?.format('YYYY-MM-DD'),
      dateTo: filters.dateRange?.[1]?.format('YYYY-MM-DD'),
      status: filters.status || undefined,
      limit: 500,
    })) as any
    const exportData = Array.isArray(axiosRes?.data?.data) ? axiosRes.data.data : []

    const headerRow = EXPORT_COLUMNS.map((c) => csvEscapeCell(c.label))
    const lines = exportData.map((item: Approval) =>
      EXPORT_COLUMNS.map(({ key }) => csvEscapeCell((item as Approval)[key])).join(',')
    )

    const csv = [headerRow.join(','), ...lines].join('\n')
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `Transporter_Report_${dayjs().format('YYYY-MM-DD')}.csv`
    document.body.appendChild(a)
    a.click()
    document.body.removeChild(a)
    window.URL.revokeObjectURL(url)
  }

  const handleExport = async () => {
    try {
      if (!filters.transporterCode) return
      await fetchExportPayload()
    } catch (error) {
      console.error('Export failed:', error)
      toast.error('Export failed')
    }
  }

  const rows = approvals ?? []

  const isPdf = useMemo(() => (viewUrl ? /\.pdf($|\?)/i.test(viewUrl) : false), [viewUrl])

  const columns = useMemo(
    () => [
      ...translateApprovalColumns(englishT),
      buildViewActionColumn({ t: englishT, onView: handleViewDocument, label: 'View' }),
    ],
    [handleViewDocument]
  )

  return (
    <PageShell>
      <PageHero
        title={resolvedTitle}
        subtitle={resolvedDescription}
        badges={[{ label: 'Transporter', accent: true, icon: Truck }]}
      />

      <FilterPanel label="Filters">
        <div className="grid w-full grid-cols-1 gap-4 md:grid-cols-4">
          {fixedTransporterCode ? (
            <div>
              <label className="mb-2 block text-sm font-medium text-foreground">Transporter Code</label>
              <Input value={filters.transporterCode} disabled />
            </div>
          ) : (
            <div>
              <label className="mb-2 block text-sm font-medium text-foreground">Transporter Code</label>
              <Input
                placeholder="Enter transporter code"
                value={filters.transporterCode}
                onChange={(e) => setFilters({ ...filters, transporterCode: e.target.value })}
              />
            </div>
          )}

          <div>
            <label className="mb-2 block text-sm font-medium text-foreground">Date Range</label>
            <RangePicker
              value={filters.dateRange}
              onChange={(dates) => setFilters({ ...filters, dateRange: dates as typeof filters.dateRange })}
              className="w-full"
            />
          </div>

          <div>
            <label className="mb-2 block text-sm font-medium text-foreground">Status</label>
            {lockedStatus ? (
              <Input value={lockedStatus} disabled />
            ) : (
              <Select
                value={filters.status || undefined}
                onChange={(value) => setFilters({ ...filters, status: value ?? '' })}
                className="w-full"
                allowClear
                placeholder="All statuses"
              >
                <Option value="Pending">Pending</Option>
                <Option value="Approved">Approved</Option>
                <Option value="Rejected">Rejected</Option>
              </Select>
            )}
          </div>

          <div className="flex flex-wrap items-end gap-2">
            <AntButton type="primary" icon={<SearchOutlined />} onClick={handleSearch}>
              Search
            </AntButton>
            <AntButton icon={<DownloadOutlined />} onClick={handleExport} disabled={!rows.length}>
              Export
            </AntButton>
          </div>
        </div>
      </FilterPanel>

      <DashboardPanel
        title="Documents"
        subtitle={
          lockedStatus === 'Pending'
            ? 'Submitted POD awaiting POD Admin — click a row for journey'
            : lockedStatus === 'Approved'
              ? 'Approved POD for your transporter code — click a row for journey'
              : 'Filtered transporter queue — click a row for history'
        }
        flush
        className="leap-data-page-card"
      >
        <LeapDataTable<Approval>
          dataSource={rows}
          columns={columns}
          rowKey="id"
          loading={isLoading}
          pagination={leapTablePagination(rows.length)}
          onRow={record => ({
            onClick: e => {
              if (isTableChromeTarget(e.target)) return
              goToDocument(record)
            },
            style: { cursor: 'pointer' },
          })}
        />
      </DashboardPanel>

      <Modal
        title="View document"
        open={viewModalOpen}
        onCancel={() => {
          setViewModalOpen(false)
          setViewUrl(null)
          setViewRecord(null)
        }}
        footer={null}
        width="min(1200px, 98vw)"
        destroyOnClose
      >
        {viewUrl ? (
          <div className="flex flex-col gap-4 md:flex-row md:items-start">
            {viewRecord ? <DocumentPreviewSummary record={viewRecord} /> : null}
            <div className="min-h-0 min-w-0 flex-1">
              {isPdf ? (
                <iframe src={viewUrl} className="w-full" style={{ height: '75vh', border: 0 }} title="Attachment" />
              ) : (
                <img src={viewUrl} alt="attachment" className="max-h-[75vh] w-full rounded object-contain" />
              )}
            </div>
          </div>
        ) : (
          <div className="text-sm text-muted-foreground">No attachment to preview.</div>
        )}
      </Modal>
    </PageShell>
  )
}

export default PendingPODReportsPage
